import React, { createContext, useContext, useReducer } from 'react'
import type { CartContextType, CartItem, CartState } from '../types/cart'
import type { Pacote } from '../types'

const initialState: CartState = {
  items: [],
  isOpen: false,
  total: 0,
  totalItems: 0
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: Pacote }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'TOGGLE_CART' }
  | { type: 'SET_CART_OPEN'; payload: boolean }

const computeTotals = (items: CartItem[]) => {
  const total = items.reduce((sum, item) => sum + item.pacote.preco * item.quantity, 0)
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0)
  return { total, totalItems }
}

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const newItem: CartItem = {
        id: `${action.payload.id}-${Date.now()}`,
        pacote: action.payload,
        quantity: 1,
        addedAt: new Date()
      }
      const items = [...state.items, newItem]
      const { total, totalItems } = computeTotals(items)
      return { ...state, items, total, totalItems, isOpen: true }
    }
    case 'REMOVE_ITEM': {
      const items = state.items.filter((item) => item.id !== action.payload)
      const { total, totalItems } = computeTotals(items)
      return { ...state, items, total, totalItems }
    }
    case 'UPDATE_QUANTITY': {
      const items = state.items.map((item) =>
        item.id === action.payload.id ? { ...item, quantity: Math.max(1, action.payload.quantity) } : item
      )
      const { total, totalItems } = computeTotals(items)
      return { ...state, items, total, totalItems }
    }
    case 'CLEAR_CART':
      return initialState
    case 'TOGGLE_CART':
      return { ...state, isOpen: !state.isOpen }
    case 'SET_CART_OPEN':
      return { ...state, isOpen: action.payload }
    default:
      return state
  }
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export const useCart = () => {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart deve ser usado dentro de CartProvider')
  return ctx
}

export const CartProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState)

  const addItem = (pacote: Pacote) => dispatch({ type: 'ADD_ITEM', payload: pacote })
  const removeItem = (itemId: string) => dispatch({ type: 'REMOVE_ITEM', payload: itemId })
  const updateQuantity = (itemId: string, quantity: number) =>
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: itemId, quantity } })
  const clearCart = () => dispatch({ type: 'CLEAR_CART' })
  const toggleCart = () => dispatch({ type: 'TOGGLE_CART' })

  const getItemQuantity = (pacoteId: string): number =>
    state.items.filter((i) => i.pacote.id === pacoteId).reduce((sum, i) => sum + i.quantity, 0)

  const value: CartContextType = {
    state,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    toggleCart,
    getItemQuantity
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

